// oxlint-disable react/only-export-components
import {
  faCheck,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Select as SelectPrimitive } from "radix-ui"
import { createContext, useContext, useState } from "react"
import type { ComponentProps, ReactNode } from "react"
import { createPortal } from "react-dom"
import { Drawer as DrawerPrimitive } from "vaul"
import { cn } from "cn"

import { useIsMobile } from "../../../hooks/use-mobile"
import { DrawerContent, DrawerHeader, DrawerTitle } from "../drawer/drawer"

/**
 * Figma-Component-Set „Select" bündelt Label-Zeile, Feld und Helfertext in
 * einem Frame — hier bewusst NICHT als Props auf `Select` nachgebaut. Diese
 * App nutzt TanStack Form (`form.AppField`) mit einem generischen
 * `FieldBase`-Wrapper (siehe `packages/ui/src/v2/components/ui/field/field.tsx`,
 * analog zu shadcns `<Field />`), der Label/Description/Required/Error für
 * alle Feldtypen einheitlich rendert (Error kommt dynamisch aus
 * `field.state.meta`, nicht aus einem statischen `invalid`-Prop). Ein
 * eigenes Label/Invalid auf `Select` würde sich damit doppeln oder
 * kollidieren — `Select` bleibt daher ein bare Radix-`Select.Root`, analog
 * zum bestehenden (v1) `Select` in `@e2n/ui`. Für Label/Description/Error
 * `Field`/`FieldContent`/`FieldLabel`/`FieldDescription`/`FieldError`
 * verwenden (siehe `select.stories.tsx`).
 *
 * Mobile (Figma „Select - Mobile"): Unterhalb von `md` wird statt des
 * Radix-Selects ein `Drawer` gerendert. Radix' Select-Content lässt sich
 * nicht in einen Drawer verlagern, daher führen die Bausteine dort Wert und
 * Offen-Zustand selbst (`SelectMobileContext`) — die Konsumenten-API bleibt
 * identisch.
 */
type SelectMobileContextValue = {
  value: string | undefined
  setValue: (value: string) => void
  open: boolean
  setOpen: (open: boolean) => void
  disabled: boolean
  valueNode: HTMLElement | null
  setValueNode: (node: HTMLElement | null) => void
}

const SelectMobileContext = createContext<SelectMobileContextValue | null>(null)

/**
 * Mobile: `SelectContent` rendert seine Kinder zusätzlich (unsichtbar) in
 * diesem Modus, damit das ausgewählte `SelectItem` seinen Text auch bei
 * geschlossenem Drawer in `SelectValue` spiegeln kann — analog zu Radix, das
 * dafür den geschlossenen Content in ein Fragment rendert.
 */
const SelectValueMirrorContext = createContext(false)

function useControllable<T>(
  value: T | undefined,
  defaultValue: T,
  onChange?: (value: T) => void
) {
  const [internal, setInternal] = useState(defaultValue)
  const isControlled = value !== undefined
  const current = isControlled ? value : internal
  function set(next: T) {
    if (!isControlled) setInternal(next)
    onChange?.(next)
  }
  return [current, set] as const
}

function Select({ ...props }: ComponentProps<typeof SelectPrimitive.Root>) {
  const isMobile = useIsMobile()

  return isMobile ? (
    <SelectMobile {...props} />
  ) : (
    <SelectPrimitive.Root data-slot="select" {...props} />
  )
}

function SelectMobile({
  value: valueProp,
  defaultValue,
  onValueChange,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  name,
  required,
  children,
}: ComponentProps<typeof SelectPrimitive.Root>) {
  const [value, setValue] = useControllable<string | undefined>(
    valueProp,
    defaultValue,
    (next) => {
      if (next !== undefined) onValueChange?.(next)
    }
  )
  const [open, setOpen] = useControllable(openProp, defaultOpen, onOpenChange)
  const [valueNode, setValueNode] = useState<HTMLElement | null>(null)

  return (
    <SelectMobileContext.Provider
      value={{
        value,
        setValue,
        open,
        setOpen,
        disabled,
        valueNode,
        setValueNode,
      }}
    >
      <DrawerPrimitive.Root
        data-slot="select"
        open={open}
        onOpenChange={setOpen}
      >
        {children}
      </DrawerPrimitive.Root>
      {name && (
        <input
          type="hidden"
          name={name}
          value={value ?? ""}
          required={required}
          disabled={disabled}
        />
      )}
    </SelectMobileContext.Provider>
  )
}

function SelectValue({
  placeholder,
  children,
  ...props
}: ComponentProps<typeof SelectPrimitive.Value>) {
  const mobile = useContext(SelectMobileContext)

  if (mobile) {
    const hasValue = mobile.value !== undefined && mobile.value !== ""
    return (
      <span
        data-slot="select-value"
        ref={children === undefined ? mobile.setValueNode : undefined}
        className="pointer-events-none"
      >
        {children ?? (hasValue ? null : placeholder)}
      </span>
    )
  }

  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      placeholder={placeholder}
      {...props}
    >
      {children}
    </SelectPrimitive.Value>
  )
}

/**
 * Farbpunkt (Figma „showDot"); Farbe per `style` wie bei `Combobox`, weil sie
 * meist erst zur Laufzeit aus den Daten kommt und Tailwind dynamische Klassen
 * nicht erzeugen kann.
 */
function SelectDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      data-slot="select-dot"
      className="size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  )
}

type SelectTriggerProps = ComponentProps<typeof SelectPrimitive.Trigger> & {
  leftIcon?: ReactNode
  showLeftIcon?: boolean
  /** Figma „showDot": CSS-Farbwert des Punkts (z. B. `#9932dd`), ohne Wert kein Punkt. */
  dotColor?: string
}

function SelectTrigger({
  className,
  leftIcon,
  showLeftIcon = false,
  dotColor,
  children,
  disabled,
  ...props
}: SelectTriggerProps) {
  const mobile = useContext(SelectMobileContext)

  const triggerClassName = cn(
    "group/select-trigger flex h-9 w-full items-center gap-1 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary pr-3 pl-4 text-left text-sm leading-5 whitespace-nowrap text-labels-primary transition-colors outline-none focus-visible:border-brand-blue-100 disabled:cursor-not-allowed disabled:border-neutrals-v2-200 disabled:text-labels-tertiary aria-invalid:border-error-red-200 data-placeholder:text-labels-secondary data-[state=open]:border-brand-blue-100",
    className
  )

  const content = (
    <>
      {/* Figma „selected item": Icon/Dot und Wert mit 8px Abstand, zum Chevron 4px. */}
      <span className="flex min-w-0 flex-1 items-center gap-2 [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate">
        {showLeftIcon && leftIcon}
        {dotColor && <SelectDot color={dotColor} />}
        {children}
      </span>
      <FontAwesomeIcon
        icon={faChevronDown}
        aria-hidden
        className="size-4.5 shrink-0 group-data-[state=open]/select-trigger:hidden"
      />
      <FontAwesomeIcon
        icon={faChevronUp}
        aria-hidden
        className="hidden size-4.5 shrink-0 group-data-[state=open]/select-trigger:block"
      />
    </>
  )

  if (mobile) {
    const hasValue = mobile.value !== undefined && mobile.value !== ""
    return (
      <DrawerPrimitive.Trigger
        data-slot="select-trigger"
        role="combobox"
        aria-expanded={mobile.open}
        aria-haspopup="listbox"
        data-placeholder={hasValue ? undefined : ""}
        disabled={disabled ?? mobile.disabled}
        className={triggerClassName}
        {...(props as ComponentProps<typeof DrawerPrimitive.Trigger>)}
      >
        {content}
      </DrawerPrimitive.Trigger>
    )
  }

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      disabled={disabled}
      className={triggerClassName}
      {...props}
    >
      {content}
    </SelectPrimitive.Trigger>
  )
}

type SelectContentProps = ComponentProps<typeof SelectPrimitive.Content> & {
  /** Titel im Drawer-Header (nur Mobile, Figma: das Label des Felds). */
  mobileTitle?: string
}

function SelectContent({
  className,
  children,
  position = "popper",
  sideOffset = 4,
  mobileTitle = "Auswählen",
  ...props
}: SelectContentProps) {
  const mobile = useContext(SelectMobileContext)

  if (mobile) {
    return (
      <>
        <SelectValueMirrorContext.Provider value>
          {children}
        </SelectValueMirrorContext.Provider>
        <DrawerContent data-slot="select-content" aria-describedby={undefined}>
          <DrawerHeader>
            <DrawerTitle>{mobileTitle}</DrawerTitle>
          </DrawerHeader>
          <div
            role="listbox"
            aria-label={mobileTitle}
            className={cn(
              "flex min-h-0 w-full flex-col gap-0.5 overflow-y-auto",
              className
            )}
          >
            {children}
          </div>
        </DrawerContent>
      </>
    )
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset}
        className={cn(
          "relative z-50 flex max-h-(--radix-select-content-available-height) min-w-32 flex-col gap-0.5 overflow-hidden rounded-xl border border-neutrals-v2-200 bg-backgrounds-primary py-2 shadow-popover outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "w-full min-w-(--radix-select-trigger-width)",
          className
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "flex flex-col gap-0.5",
            position === "popper" && "w-full"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

type SelectItemProps = ComponentProps<typeof SelectPrimitive.Item> & {
  leftIcon?: ReactNode
  showLeftIcon?: boolean
  /** Figma „showDot": CSS-Farbwert des Punkts (z. B. `#9932dd`), ohne Wert kein Punkt. */
  dotColor?: string
}

const itemClassName =
  "flex w-full cursor-pointer items-center gap-2 rounded-xl px-4 py-2 text-sm leading-5 text-labels-primary outline-none select-none"

/**
 * Figma unterscheidet „hover" (Zeiger) und „focus" (Tastatur) mit
 * unterschiedlicher Optik (Hintergrund vs. Border) — Radix meldet beides
 * einheitlich über `data-highlighted`. Hier bewusst auf den
 * Hintergrund-Stil vereinheitlicht statt eine nicht verfügbare
 * Unterscheidung nachzubauen.
 */
function SelectItem({
  className,
  leftIcon,
  showLeftIcon = false,
  dotColor,
  children,
  value,
  disabled,
  ...props
}: SelectItemProps) {
  const mobile = useContext(SelectMobileContext)
  const isMirror = useContext(SelectValueMirrorContext)

  const decoration = (
    <>
      {showLeftIcon && leftIcon}
      {dotColor && <SelectDot color={dotColor} />}
    </>
  )
  const check = (
    <FontAwesomeIcon
      icon={faCheck}
      className="size-4.5 shrink-0 text-brand-red-200"
    />
  )

  if (mobile) {
    const isSelected = mobile.value === value
    if (isMirror) {
      return isSelected && mobile.valueNode
        ? createPortal(children, mobile.valueNode)
        : null
    }
    return (
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        data-slot="select-item"
        data-state={isSelected ? "checked" : "unchecked"}
        disabled={disabled}
        className={cn(
          itemClassName,
          "text-left hover:bg-neutrals-v2-200 focus-visible:bg-neutrals-v2-200 disabled:pointer-events-none disabled:text-labels-tertiary",
          className
        )}
        onClick={() => {
          mobile.setValue(value)
          mobile.setOpen(false)
        }}
      >
        {decoration}
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {isSelected && check}
      </button>
    )
  }

  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      value={value}
      disabled={disabled}
      className={cn(
        itemClassName,
        "data-disabled:pointer-events-none data-disabled:text-labels-tertiary data-highlighted:bg-neutrals-v2-200",
        className
      )}
      {...props}
    >
      {decoration}
      {/* Radix verwirft `className`/`style` auf `ItemText` (der Text wird per Portal in den Trigger gespiegelt) — Layout daher über diesen eigenen Wrapper statt direkt auf `ItemText`. */}
      <span className="min-w-0 flex-1 truncate">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </span>
      <SelectPrimitive.ItemIndicator asChild>
        {check}
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

/**
 * Figma-Varianten „reset button"/„clear button" (beide zeigen „Auswahl
 * entfernen") — kein Radix-`Select.Item`, da Radix keinen leeren
 * Wert-String als Item erlaubt. Der Konsument steuert das Zurücksetzen
 * selbst über `onClick`/`onSelect`.
 */
function SelectClearItem({ className, ...props }: ComponentProps<"button">) {
  const isMirror = useContext(SelectValueMirrorContext)
  if (isMirror) return null

  return (
    <button
      type="button"
      data-slot="select-clear-item"
      className={cn(
        "flex w-full cursor-pointer items-center rounded-xl border-2 border-transparent px-4 py-2 text-left text-sm leading-5 font-semibold text-labels-primary outline-none hover:bg-neutrals-v2-200 focus-visible:border-brand-blue-100 focus-visible:hover:bg-transparent",
        className
      )}
      {...props}
    />
  )
}

export {
  Select,
  SelectClearItem,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
}
export type { SelectContentProps, SelectItemProps, SelectTriggerProps }
